import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { formatDate } from "../../../../../utils/date"
import type { Verdict } from "../../studio/studioTypes"
import { SupplierAssignControl } from "../supplier/SupplierAssignControl"
import type {
  ApproveBlockingWorkItem,
  ApproveGateDetail,
  ApprovePendingCounters,
} from "./approveGateTypes"
import { issueReasonKey } from "./issueReason"

export interface ApproveGatePanelProps {
  detail: ApproveGateDetail
  nameOf: (framework: string) => string
  /** Only read by the NO_SUPPLIER branch, to mount SupplierAssignControl
   *  right there — every other branch ignores these two. */
  declId: string
  supplierMentioned?: string | null
  /** SUPPLIER_CHANGED only (MC_SUPPLIER_REASSIGN): re-extracts the declaration
   *  against the bound supplier, the reassignment's own chain. Absent (no
   *  permission to re-extract): the copy alone. */
  onReextract?: () => void
  /** `step` (W5-4): the blocking step the jump names, so the landed card
   *  also opens that step's own dialog (`UnitCard`'s own `focusStep`).
   *  PENDING_ITEMS passes the LOWEST (most urgent) of a statement's own
   *  blocking steps when it dedupes several into one button; MISSING_LIST_VERSION
   *  carries no step on the wire and omits it. */
  onGoToStatement: (
    framework: string,
    statementId: string,
    step?: 1 | 2 | 3,
  ) => void
  /** REVIEW_PENDING only: an automatic retry is still owed, so the wait is
   *  worded as one the screen finishes itself. False (the default, and what
   *  every branch other than REVIEW_PENDING gets) keeps the manual copy —
   *  which is also what an exhausted retry budget falls back to, so the panel
   *  never promises a press that is no longer coming. */
  autoRetrying?: boolean
  /** RECOMPUTE_RUNNING / RECOMPUTE_DROPPED only: the recalculation this refusal
   *  waited for has been SEEN running and has since ended. The manual copy says
   *  "in a moment", which after the automatic wait's three minutes left the
   *  reviewer guessing when; this says it is over (prod 2026-10-19: the walk
   *  ended 90 s after the wait gave up, nothing said so, and the declaration
   *  stayed unapproved). Never set from a status that was only ever idle — an
   *  idle line beside a running guard means unknown, not finished. */
  recomputeFinished?: boolean
  /** Same `labelOf` `OpenPointsPopover` reads (TUR-1211 W2-3b) — the gate's
   *  own "Zur Aussage N" buttons name the card by its DISPLAY position, not
   *  the backend payload's merge-order `group_index`, so a reviewer who just
   *  read the cards top-to-bottom lands where the number says. Both
   *  PENDING_ITEMS' `blocking_work` and MISSING_LIST_VERSION's `statements`
   *  already carry the statement id straight off the wire — no groupIndex
   *  resolution needed, `labelOf` is called with the id it always has.
   *  Optional so every existing render/test keeps the old `group_index + 1`
   *  number. */
  labelOf?: (
    framework: string,
    statementId: string,
    groupIndex: number | undefined,
  ) => number
  /** Same `verdictOf` `OpenPointsPopover` reads (TUR-1676): PENDING_ITEMS
   *  words a statement's step 3 by its verdict, as the map does. Optional so
   *  every existing render/test keeps the substances wording. */
  verdictOf?: (statementId: string) => Verdict | null | undefined
}

const DEAD_ADDRESS_STATUSES = new Set(["undeliverable", "complained"])

function groupByFramework(
  items: readonly ApproveBlockingWorkItem[],
): Map<string, ApproveBlockingWorkItem[]> {
  const out = new Map<string, ApproveBlockingWorkItem[]>()
  for (const item of items) {
    const list = out.get(item.framework)
    if (list) list.push(item)
    else out.set(item.framework, [item])
  }
  return out
}

/**
 * The approve-refusal gate, rendered INLINE ABOVE the footer bar (never a
 * modal — the reviewer stays on the workspace they were just working in).
 * CONTENT-FRAMED, ALWAYS: every branch names what is missing and closes with
 * the same alternatives line, because Anfordern and Überspringen stay
 * reachable no matter which 409 code came back — the journey never
 * dead-ends on a refusal it cannot itself resolve.
 */
/** `OpenPointsPopover`'s own default — every existing render/test that omits
 *  `labelOf` keeps today's plain `group_index + 1` number. */
const DEFAULT_LABEL_OF: NonNullable<ApproveGatePanelProps["labelOf"]> = (
  _framework,
  _statementId,
  groupIndex,
) => (groupIndex ?? 0) + 1

/** Every existing render/test that omits `verdictOf` resolves no verdict and
 *  keeps the substances wording. */
const DEFAULT_VERDICT_OF: NonNullable<
  ApproveGatePanelProps["verdictOf"]
> = () => undefined

export function ApproveGatePanel({
  detail,
  nameOf,
  declId,
  supplierMentioned,
  onReextract,
  onGoToStatement,
  autoRetrying = false,
  recomputeFinished = false,
  labelOf = DEFAULT_LABEL_OF,
  verdictOf = DEFAULT_VERDICT_OF,
}: ApproveGatePanelProps) {
  const { translate } = useTranslation()

  return (
    <div
      data-testid="approve-gate-panel"
      className="shrink-0 border-t border-attention/30 bg-attention/5 px-4 py-3"
    >
      <p className="text-[13px] font-semibold text-white">
        {translate("MC_DECL_V2_GATE_TITLE")}
      </p>
      <div className="mt-2 space-y-2 text-[12px] text-grey5">
        {detail.code === "PENDING_ITEMS" && (
          <PendingItemsBody
            detail={detail}
            nameOf={nameOf}
            onGoToStatement={onGoToStatement}
            labelOf={labelOf}
            verdictOf={verdictOf}
          />
        )}
        {detail.code === "OPEN_INQUIRY" && (
          <OpenInquiryBody detail={detail} nameOf={nameOf} />
        )}
        {detail.code === "REVIEW_PENDING" && (
          <p>
            {translate(
              // TUR-1725: the review job sits on the worker without a live worker
              // taking it; the footer stops retrying, so no "automatically" copy.
              detail.review_delayed
                ? "MC_DECL_V2_GATE_REVIEW_DELAYED"
                : autoRetrying
                  ? "MC_DECL_V2_GATE_REVIEW_PENDING_AUTO"
                  : "MC_DECL_V2_GATE_REVIEW_PENDING",
            )}
          </p>
        )}
        {detail.code === "RECOMPUTE_RUNNING" && (
          <p>
            {translate(
              // TUR-1725: the declaration changed under approve (`gates_moved`),
              // no recalculation ran: say so, not "still being recalculated".
              detail.gates_moved
                ? autoRetrying
                  ? "MC_DECL_V2_GATE_GATES_MOVED_AUTO"
                  : "MC_DECL_V2_GATE_GATES_MOVED"
                : autoRetrying
                  ? "MC_DECL_V2_GATE_RECOMPUTE_RUNNING_AUTO"
                  : recomputeFinished
                    ? "MC_DECL_V2_GATE_RECOMPUTE_FINISHED"
                    : "MC_DECL_V2_GATE_RECOMPUTE_RUNNING",
            )}
          </p>
        )}
        {detail.code === "RECOMPUTE_DROPPED" && (
          <p>
            {translate(
              autoRetrying
                ? "MC_DECL_V2_GATE_RECOMPUTE_DROPPED_AUTO"
                : recomputeFinished
                  ? "MC_DECL_V2_GATE_RECOMPUTE_FINISHED"
                  : "MC_DECL_V2_GATE_RECOMPUTE_DROPPED",
            )}
          </p>
        )}
        {detail.code === "RECOMPUTE_FAILED" && (
          <p>{translate("MC_DECL_V2_GATE_RECOMPUTE_FAILED")}</p>
        )}
        {detail.code === "NO_SUPPLIER" && (
          <NoSupplierBody
            declId={declId}
            supplierMentioned={supplierMentioned ?? null}
          />
        )}
        {detail.code === "SUPPLIER_CHANGED" && (
          <SupplierChangedBody onReextract={onReextract} />
        )}
        {detail.code === "MISSING_LIST_VERSION" && (
          <MissingListVersionBody
            detail={detail}
            onGoToStatement={onGoToStatement}
            labelOf={labelOf}
          />
        )}
        {detail.code === "NO_WRITABLE_ARTICLES" && (
          <p>{translate("MC_DECL_V2_GATE_NO_WRITABLE_ARTICLES")}</p>
        )}
        {/* Unknown codes get the translated generic line, NEVER the backend's
            `message`: that string is English developer prose ("There are no
            articles to write compliance data to ...") and landed verbatim in
            the German UI (product-owner screenshot, 28.08.). */}
        {detail.code !== "PENDING_ITEMS" &&
          detail.code !== "OPEN_INQUIRY" &&
          detail.code !== "REVIEW_PENDING" &&
          detail.code !== "RECOMPUTE_RUNNING" &&
          detail.code !== "RECOMPUTE_DROPPED" &&
          detail.code !== "RECOMPUTE_FAILED" &&
          detail.code !== "NO_SUPPLIER" &&
          detail.code !== "SUPPLIER_CHANGED" &&
          detail.code !== "MISSING_LIST_VERSION" &&
          detail.code !== "NO_WRITABLE_ARTICLES" && (
            <p>{translate("MC_DECL_V2_GATE_GENERIC")}</p>
          )}
      </div>
      {/* `.sv2-gate` — the same quiet gate-hint role the footer's own inline
          hint uses (studioV2.css), rather than an ad-hoc size/color pair
          duplicating that role's look by coincidence. */}
      <p className="sv2-gate mt-2.5 border-t border-grey4 pt-2">
        {translate("MC_DECL_V2_GATE_ALTERNATIVES")}
      </p>
    </div>
  )
}

function MissingListVersionBody({
  detail,
  onGoToStatement,
  labelOf,
}: {
  detail: ApproveGateDetail
  onGoToStatement: (framework: string, statementId: string) => void
  labelOf: NonNullable<ApproveGatePanelProps["labelOf"]>
}) {
  const { translate } = useTranslation()
  const statements = detail.statements ?? []
  if (statements.length === 0) {
    return <p>{translate("MC_DECL_V2_GATE_GENERIC")}</p>
  }
  return (
    <div>
      <p>{translate("MC_DECL_V2_GATE_MISSING_LIST_VERSION_INTRO")}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {[...statements]
          .sort(
            (a, b) =>
              labelOf(a.framework, a.statement_id, a.group_index) -
              labelOf(b.framework, b.statement_id, b.group_index),
          )
          .map((statement) => (
            <button
              key={statement.statement_id}
              type="button"
              className="sv2-btn sv2-btn-sm"
              onClick={() =>
                onGoToStatement(statement.framework, statement.statement_id)
              }
            >
              {translate("MC_DECL_V2_GATE_GO_TO_STATEMENT", {
                n: labelOf(
                  statement.framework,
                  statement.statement_id,
                  statement.group_index,
                ),
              })}
            </button>
          ))}
      </div>
    </div>
  )
}

/** The PENDING_ITEMS counters that can refuse on their own, each with the line
 *  that names it, under a heading that says these are what is still open.
 *  `unresolved_review` is deliberately absent: it is `blocking_work`'s length,
 *  so it is always zero in the branch that reads this, and a line for it could
 *  only ever be dead copy. `missing_cas_claims` is absent for the opposite
 *  reason: since F3b it rides along on the payload but cannot refuse, so
 *  listing it here would name a non-blocker as the blocker. */
const PENDING_COUNTERS: ReadonlyArray<
  [keyof ApprovePendingCounters, TranslationKeys]
> = [
  ["unresolved_conflicts", "MC_DECL_V2_GATE_PENDING_CONFLICTS"],
  ["interface_gaps", "MC_DECL_V2_GATE_PENDING_INTERFACE_GAPS"],
]

function PendingItemsBody({
  detail,
  nameOf,
  onGoToStatement,
  labelOf,
  verdictOf,
}: {
  detail: ApproveGateDetail
  nameOf: (framework: string) => string
  onGoToStatement: (
    framework: string,
    statementId: string,
    step?: 1 | 2 | 3,
  ) => void
  labelOf: NonNullable<ApproveGatePanelProps["labelOf"]>
  verdictOf: NonNullable<ApproveGatePanelProps["verdictOf"]>
}) {
  const { translate } = useTranslation()
  const items = detail.blocking_work ?? []
  if (items.length === 0) {
    // NOT AUTOMATICALLY GENERIC. The server refuses on three counters and
    // itemises only one of them (`unresolved_review`, which IS
    // `blocking_work`); the other two refused with nothing on screen to act
    // on (Vishay, 16.09: an empty `blocking_work`, the generic "try again
    // later" and an hour of auto-retries). Naming the counter is not the
    // itemised worklist, but it is the difference between a reviewer who
    // knows what to look for and one who does not. That incident's own
    // counter, `missing_cas_claims`, no longer refuses at all (F3b).
    const counted = PENDING_COUNTERS.filter(
      ([field]) => (detail.pending?.[field] ?? 0) > 0,
    )
    if (counted.length === 0) {
      return <p>{translate("MC_DECL_V2_GATE_GENERIC")}</p>
    }
    return (
      <div>
        <p>{translate("MC_DECL_V2_GATE_PENDING_INTRO")}</p>
        <ul className="ml-3 mt-1.5 list-disc space-y-0.5">
          {counted.map(([field, key]) => (
            <li key={field}>
              {translate(key, { n: detail.pending?.[field] ?? 0 })}
            </li>
          ))}
        </ul>
      </div>
    )
  }
  const byFramework = groupByFramework(items)
  return (
    <div>
      <p>{translate("MC_DECL_V2_GATE_PENDING_INTRO")}</p>
      <ul className="mt-1.5 space-y-1.5">
        {[...byFramework.entries()].map(([fw, fwItems]) => {
          // ONE jump button per STATEMENT, not per step: a statement can
          // carry two `blocking_work` items at once (e.g. its step-2 scope
          // AND its step-3 condition fields both still open): a `Map`
          // dedupes those onto a single button, keyed by `statement_id`, in
          // first-seen order, KEEPING THE LOWEST (most urgent) step: the
          // SAME priority the collapsed card's own open-points chip uses
          // (`UnitCard`'s `openIssues[0]`), so the jump opens the dialog
          // that fixes the statement's FIRST blocker, not merely its last.
          // Framework-unit issues (`unit: "framework"`, the step-1
          // reference-list conflict: see `WorklistIssue`'s own doc comment
          // in `mc_declaration_cell_state.py`) carry no `statement_id` and
          // are correctly skipped: there is no statement to jump to.
          const statements = new Map<
            string,
            { groupIndex: number; step: 1 | 2 | 3 }
          >()
          for (const item of fwItems) {
            if (item.statement_id !== null && item.group_index !== null) {
              const prev = statements.get(item.statement_id)
              if (!prev || item.step < prev.step) {
                statements.set(item.statement_id, {
                  groupIndex: item.group_index,
                  step: item.step,
                })
              }
            }
          }
          return (
            <li key={fw}>
              <p className="text-white">{nameOf(fw)}</p>
              <ul className="ml-3 list-disc space-y-0.5">
                {fwItems.map((item, i) => (
                  <li key={`${item.unit_id}:${item.step}:${i}`}>
                    {translate(
                      issueReasonKey(
                        item,
                        item.statement_id === null
                          ? undefined
                          : verdictOf(item.statement_id),
                      ).label,
                    )}
                    {item.article_numbers.length > 0 &&
                      ` · ${translate("MC_DECL_V2_GATE_ARTICLE_COUNT", { n: item.article_numbers.length })}`}
                  </li>
                ))}
              </ul>
              {statements.size > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {/* Buttons in DISPLAY-number order: the payload lists
                      blockers in merge order, and "Aussage 3, 1, 2" beside
                      a map and a Weiter that walk 1, 2, 3 reads as a
                      different list (review, wave 2). */}
                  {[...statements.entries()]
                    .sort(
                      ([idA, a], [idB, b]) =>
                        labelOf(fw, idA, a.groupIndex) -
                        labelOf(fw, idB, b.groupIndex),
                    )
                    .map(([statementId, { groupIndex, step }]) => (
                      <button
                        key={statementId}
                        type="button"
                        className="sv2-btn sv2-btn-sm"
                        onClick={() => onGoToStatement(fw, statementId, step)}
                      >
                        {translate("MC_DECL_V2_GATE_GO_TO_STATEMENT", {
                          n: labelOf(fw, statementId, groupIndex),
                        })}
                      </button>
                    ))}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function OpenInquiryBody({
  detail,
  nameOf,
}: {
  detail: ApproveGateDetail
  nameOf: (framework: string) => string
}) {
  const { translate } = useTranslation()
  const rows = detail.open_inquiries ?? []
  // TOLERATE A MISSING/EMPTY LIST — the code alone still says why the
  // approval is refused even if the per-row detail did not arrive; an empty
  // itemised list here would be the dead end the product rule forbids.
  if (rows.length === 0) {
    return <p>{translate("MC_DECL_V2_GATE_OPEN_INQUIRY_EMPTY")}</p>
  }
  return (
    <div>
      <p>{translate("MC_DECL_V2_GATE_OPEN_INQUIRY_INTRO")}</p>
      <ul className="mt-1.5 space-y-1">
        {rows.map((row) => (
          <li key={row.inquiry_id}>
            <span className="text-white">
              {row.framework_slug
                ? nameOf(row.framework_slug)
                : translate("MC_DECL_V2_REQUEST_NO_FRAMEWORK")}
            </span>
            {" · "}
            {DEAD_ADDRESS_STATUSES.has(row.status)
              ? translate("MC_DECL_V2_GATE_INQUIRY_DEAD")
              : translate("MC_DECL_V2_GATE_INQUIRY_WAITING", {
                  date: formatDate(row.sent_at) || "—",
                })}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * MC_DECL_V2_SUP — the declaration names an issuer master data does not know
 * (approve() 409 NO_SUPPLIER). Names why THIS way of approving is refused,
 * then mounts SupplierAssignControl right here so the reviewer never has to
 * leave the panel to resolve it — the same "content-framed, never a dead
 * end" contract every other branch above follows.
 */
/** approve() 409 SUPPLIER_CHANGED: the bound supplier is not the one the
 *  declaration was evaluated against (a reassignment whose re-extraction never
 *  ran or failed). Re-picking the bound supplier changes nothing, so the way
 *  out is the re-extraction itself. */
function SupplierChangedBody({ onReextract }: { onReextract?: () => void }) {
  const { translate } = useTranslation()
  return (
    <div>
      <p>{translate("MC_DECL_V2_GATE_SUPPLIER_CHANGED")}</p>
      {onReextract && (
        <div className="mt-2">
          <button
            type="button"
            className="sv2-btn sv2-btn-sm"
            onClick={onReextract}
          >
            {translate("MC_DECL_V2_GATE_SUPPLIER_CHANGED_CTA")}
          </button>
        </div>
      )}
    </div>
  )
}

function NoSupplierBody({
  declId,
  supplierMentioned,
}: {
  declId: string
  supplierMentioned: string | null
}) {
  const { translate } = useTranslation()
  return (
    <div>
      <p>{translate("MC_DECL_V2_SUP_GATE_INTRO")}</p>
      <div className="mt-2">
        <SupplierAssignControl
          declId={declId}
          supplierMentioned={supplierMentioned}
          trigger={
            <button type="button" className="sv2-btn sv2-btn-sm">
              {translate("MC_DECL_V2_SUP_ASSIGN_CTA")}
            </button>
          }
        />
      </div>
    </div>
  )
}
